import { useEffect, useRef, useState } from 'react';
import { errorReportsApi, type ErrorReportPayload } from '@/lib/api';
import { PLATFORM_PHONE, PLATFORM_PHONE_HREF } from '@/lib/platformContact';

interface Props {
    open: boolean;
    onClose: () => void;
    source: 'login' | 'institute' | 'server_exe';
    /** Pre-fill (and lock) the institute name when it's known (e.g. from the EXE). */
    defaultInstituteName?: string;
    defaultInstituteId?: string | null;
    /** Pre-fill reporter identity when a user is signed in. */
    defaultName?: string;
    defaultEmail?: string;
}

const MAX_IMAGE_BYTES = 3 * 1024 * 1024;

export default function ReportAdminModal({
    open, onClose, source, defaultInstituteName, defaultInstituteId, defaultName, defaultEmail,
}: Props) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [institute, setInstitute] = useState('');
    const [errorText, setErrorText] = useState('');
    const [image, setImage] = useState<string | null>(null);
    const [imageName, setImageName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState('');
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            setName(defaultName || '');
            setEmail(defaultEmail || '');
            setInstitute(defaultInstituteName || '');
            setErrorText('');
            setImage(null);
            setImageName('');
            setDone(false);
            setErr('');
            setSubmitting(false);
        }
    }, [open, defaultName, defaultEmail, defaultInstituteName]);

    if (!open) return null;

    const handleFile = (file: File | undefined) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) { setErr('Please attach an image file.'); return; }
        if (file.size > MAX_IMAGE_BYTES) { setErr('Image is too large (max 3 MB).'); return; }
        const reader = new FileReader();
        reader.onload = () => { setImage(String(reader.result)); setImageName(file.name); setErr(''); };
        reader.onerror = () => setErr('Could not read that image.');
        reader.readAsDataURL(file);
    };

    const submit = async () => {
        if (!errorText.trim()) { setErr('Please describe the error you are seeing.'); return; }
        setSubmitting(true);
        setErr('');
        try {
            const payload: ErrorReportPayload = {
                reporter_name: name.trim() || undefined,
                reporter_email: email.trim() || undefined,
                institute_name: institute.trim() || undefined,
                institute_id: defaultInstituteId ?? undefined,
                error_text: errorText.trim(),
                screenshot_data: image,
                source,
            };
            await errorReportsApi.submit(payload);
            setDone(true);
        } catch (e: any) {
            setErr(e?.message || 'Could not send your report. Please check your connection and try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
            <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div>
                        <h2 className="text-base font-bold text-slate-900 dark:text-white">Report a problem to the Admin</h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Your report goes straight to the platform administrator. Urgent?
                            Call E-Swift on{' '}
                            <a href={PLATFORM_PHONE_HREF} className="font-medium text-indigo-600 dark:text-indigo-400">
                                {PLATFORM_PHONE}
                            </a>.
                        </p>
                    </div>
                    <button type="button" aria-label="Close" onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {done ? (
                    <div className="px-6 py-10 flex flex-col items-center text-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
                            <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        </div>
                        <p className="font-semibold text-slate-800 dark:text-slate-100">Report sent</p>
                        <p className="text-sm text-slate-500 max-w-xs">Thank you — the administrator has received your report and will look into it.</p>
                        <p className="text-xs text-slate-400 max-w-xs">
                            If it cannot wait, call E-Swift on{' '}
                            <a href={PLATFORM_PHONE_HREF} className="font-medium text-slate-500 dark:text-slate-300">{PLATFORM_PHONE}</a>.
                        </p>
                        <button type="button" onClick={onClose} className="mt-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium">Done</button>
                    </div>
                ) : (
                    <div className="px-6 py-5 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className="block">
                                <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Your name</span>
                                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ramesh Kumar"
                                    className="mt-1 w-full input-field text-sm" />
                            </label>
                            <label className="block">
                                <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Your email</span>
                                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@example.com"
                                    className="mt-1 w-full input-field text-sm" />
                            </label>
                        </div>

                        <label className="block">
                            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Institute name</span>
                            <input value={institute} onChange={(e) => setInstitute(e.target.value)}
                                readOnly={!!defaultInstituteName}
                                placeholder="Your institute"
                                className={`mt-1 w-full input-field text-sm ${defaultInstituteName ? 'opacity-70 cursor-not-allowed' : ''}`} />
                            {defaultInstituteName && <span className="text-[11px] text-slate-400">Detected automatically from this server.</span>}
                        </label>

                        <label className="block">
                            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">What went wrong? <span className="text-rose-500">*</span></span>
                            <textarea value={errorText} onChange={(e) => setErrorText(e.target.value)} rows={4}
                                placeholder="Describe the error, what you were doing, and any message shown on screen."
                                className="mt-1 w-full input-field text-sm resize-y" />
                        </label>

                        <div>
                            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Attach a screenshot (optional)</span>
                            <input ref={fileRef} type="file" accept="image/*" className="hidden"
                                onChange={(e) => handleFile(e.target.files?.[0])} />
                            {image ? (
                                <div className="mt-1 flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-2">
                                    <img src={image} alt="attachment preview" className="h-14 w-14 object-cover rounded" />
                                    <span className="text-xs text-slate-500 truncate flex-1" title={String(imageName || '')}>{imageName}</span>
                                    <button type="button" onClick={() => { setImage(null); setImageName(''); if (fileRef.current) fileRef.current.value = ''; }}
                                        className="text-xs text-rose-600 hover:underline">Remove</button>
                                </div>
                            ) : (
                                <button type="button" onClick={() => fileRef.current?.click()}
                                    className="mt-1 w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 py-3 text-sm text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                    Click to upload an image
                                </button>
                            )}
                        </div>

                        {err && <p className="text-sm text-rose-600 bg-rose-50 dark:bg-rose-500/10 rounded-lg px-3 py-2">{err}</p>}

                        <div className="flex justify-end gap-3 pt-1">
                            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">Cancel</button>
                            <button type="button" onClick={submit} disabled={submitting}
                                className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-60">
                                {submitting ? 'Sending…' : 'Send report'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
